import { Link } from 'react-router-dom';
import { type comics as comicsData } from '@/mocks/comics';

type Comic = (typeof comicsData)[number];

interface ComicCardProps {
  comic: Comic;
  variant?: 'default' | 'featured' | 'compact';
}

export default function ComicCard({ comic, variant = 'default' }: ComicCardProps) {
  if (variant === 'compact') {
    return (
      <Link
        to={`/comic/${comic.id}`}
        className="flex items-center gap-3 p-3 rounded-lg hover:bg-background-100 transition-colors duration-200 cursor-pointer group"
      >
        <div className="w-14 h-18 flex-shrink-0 rounded-md overflow-hidden">
          <img
            src={comic.cover}
            alt={comic.title}
            className="w-full h-full object-cover"
            width={56}
            height={72}
          />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-medium text-foreground-900 truncate group-hover:text-primary-600 transition-colors duration-200">{comic.title}</h4>
          <p className="text-xs text-foreground-500 mt-0.5">{comic.author}</p>
          <div className="flex items-center gap-2 mt-1">
            <span className="text-xs text-accent-600 font-medium">{comic.rating}分</span>
            <span className="text-xs text-foreground-400">{comic.views}</span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'featured') {
    return (
      <Link
        to={`/comic/${comic.id}`}
        className="block group cursor-pointer"
      >
        <div className="relative rounded-xl overflow-hidden aspect-[3/4]">
          <img
            src={comic.cover}
            alt={comic.title}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            width={450}
            height={600}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-4">
            <div className="flex items-center gap-2 mb-1">
              {comic.tags.slice(0, 2).map((tag) => (
                <span key={tag} className="px-2 py-0.5 text-[10px] bg-background-50/20 backdrop-blur-sm text-background-50 rounded-full">
                  {tag}
                </span>
              ))}
              <span className="px-2 py-0.5 text-[10px] bg-accent-500 text-background-50 rounded-full">{comic.status}</span>
            </div>
            <h3 className="text-white font-bold text-lg leading-tight">{comic.title}</h3>
            <div className="flex items-center gap-3 mt-1 text-xs text-background-200">
              <span className="flex items-center gap-1"><i className="ri-star-fill text-accent-400"></i>{comic.rating}</span>
              <span>{comic.views}人气</span>
            </div>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/comic/${comic.id}`}
      className="block group cursor-pointer"
    >
      <div className="relative rounded-lg overflow-hidden aspect-[3/4]">
        <img
          src={comic.cover}
          alt={comic.title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          width={300}
          height={400}
        />
        <div className="absolute top-2 right-2">
          <span className="px-2 py-0.5 text-[10px] bg-background-950/70 text-background-50 rounded-full backdrop-blur-sm">
            {comic.rating}分
          </span>
        </div>
        {comic.featured && (
          <div className="absolute top-2 left-2">
            <span className="px-2 py-0.5 text-[10px] bg-primary-500 text-background-50 rounded-full">
              推荐
            </span>
          </div>
        )}
      </div>
      <div className="mt-2.5">
        <h4 className="text-sm font-medium text-foreground-900 truncate group-hover:text-primary-600 transition-colors duration-200">{comic.title}</h4>
        <p className="text-xs text-foreground-500 mt-0.5">{comic.author}</p>
        <div className="flex items-center gap-2 mt-1">
          <span className="text-[11px] text-foreground-400">{comic.chapters}话</span>
          <span className="text-[11px] text-background-400">·</span>
          <span className="text-[11px] text-foreground-400">{comic.status}</span>
        </div>
      </div>
    </Link>
  );
}